import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import authService from '../../services/auth/authService';
import api, { disciplinesApi } from '../../services/api';

/**
 * Teams management panel for the User Management page.
 *
 * Lets managers/admins create teams and assign users to them. A user can belong
 * to many teams (team_members join table); each team's "Assign User" dropdown
 * offers users not already in that team. Backend: /api/teams.
 *
 * Teams are independent of the email "Recipient Groups" (user_groups / group_id).
 *
 * @param {() => void} onMembershipChange - called after any team/membership change
 *   so the parent can refresh the users table (the "Team" column).
 */
const TeamManagementPanel = ({ onMembershipChange }) => {
  const theme = useTheme();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [teamDisciplineId, setTeamDisciplineId] = useState('');
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  // Pending "Assign User" selection per team card, keyed by team id: { [teamId]: userId }
  const [selectedUserByTeam, setSelectedUserByTeam] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  // Keep pending selections valid: once a selected user becomes a member of that
  // team (or the team is gone), drop the stale per-team selection.
  useEffect(() => {
    setSelectedUserByTeam(prev => {
      let changed = false;
      const next = {};
      for (const [tid, uid] of Object.entries(prev)) {
        const team = teams.find(t => String(t.id) === String(tid));
        const alreadyMember = team && (team.users || []).some(m => m.id === uid);
        if (team && !alreadyMember) next[tid] = uid;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [teams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [teamsRes, usersRes, disciplinesRes] = await Promise.all([
        api.get('/teams/details'),
        authService.getUsers(true),
        disciplinesApi.getDisciplines(),
      ]);
      setTeams(teamsRes.data);
      setUsers(usersRes.users);
      setAvailableDisciplines(Array.isArray(disciplinesRes.data) ? disciplinesRes.data : []);
    } catch (err) {
      setError('Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  // Refresh this panel AND notify the parent so the Users table "Team" column updates.
  const refreshAll = () => {
    fetchData();
    if (onMembershipChange) onMembershipChange();
  };

  const handleOpen = (team = null) => {
    setSelectedTeam(team);
    setTeamName(team ? team.name : '');
    setTeamDescription(team ? (team.description || '') : '');
    setTeamDisciplineId(team && team.discipline_id ? team.discipline_id : '');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedTeam(null);
    setTeamName('');
    setTeamDescription('');
    setTeamDisciplineId('');
  };

  const handleSave = async () => {
    try {
      const payload = { name: teamName, description: teamDescription, discipline_id: teamDisciplineId || null };
      if (selectedTeam) {
        await api.put(`/teams/${selectedTeam.id}`, payload);
      } else {
        await api.post('/teams', payload);
      }
      refreshAll();
      handleClose();
    } catch (err) {
      setError('Failed to save team. A team with this name may already exist.');
    }
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Delete this team? Its members will be removed from it (their other teams and permissions are unaffected).')) {
      return;
    }
    try {
      await api.delete(`/teams/${teamId}`);
      refreshAll();
    } catch (err) {
      setError('Failed to delete team');
    }
  };

  const handleAssignUser = async (teamId) => {
    const userId = selectedUserByTeam[teamId];
    if (!userId) return;
    try {
      await api.post(`/teams/${teamId}/assign-user`, { userId });
      setSelectedUserByTeam(prev => {
        const next = { ...prev };
        delete next[teamId];
        return next;
      });
      refreshAll();
    } catch (err) {
      setError('Failed to assign user');
    }
  };

  const handleUnassignUser = async (teamId, userId, userName) => {
    if (!window.confirm(`Remove ${userName || 'this user'} from this team? Their other teams, disciplines and permissions are unaffected.`)) {
      return;
    }
    try {
      await api.post(`/teams/${teamId}/unassign-user`, { userId });
      refreshAll();
    } catch (err) {
      setError('Failed to remove user from team');
    }
  };

  // Users that can be added to a given team: not already a member of THIS team
  // AND (if the team is scoped to a discipline) possessing that discipline. A
  // user may hold several disciplines; they match if the team's is among them.
  // A user can be in multiple teams, so membership in other teams is irrelevant.
  const getAssignableUsers = (team) => {
    const memberIds = new Set((team.users || []).map(m => m.id));
    return users.filter(u =>
      !memberIds.has(u.id) &&
      (!team.discipline_id || (u.disciplines || []).some(d => d.id === team.discipline_id))
    );
  };

  return (
    <Paper sx={{ p: 2, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <GroupsIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Teams
          </Typography>
          <Chip
            label={teams.length}
            size="small"
            color={teams.length > 0 ? 'primary' : 'default'}
            variant={teams.length > 0 ? 'filled' : 'outlined'}
          />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {expanded && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Create Team
            </Button>
          )}
          <Button size="small" onClick={() => setExpanded(e => !e)}>
            {expanded ? 'Hide' : 'Manage'}
          </Button>
        </Stack>
      </Stack>

      {!expanded && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Organize users into teams and assign members. Each team is scoped to a discipline, and a user can belong to multiple teams.
        </Typography>
      )}

      {expanded && (
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : teams.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 5,
                backgroundColor: alpha(theme.palette.grey[100], 0.4),
                borderRadius: 2,
                border: `2px dashed ${alpha(theme.palette.grey[400], 0.5)}`
              }}
            >
              <GroupsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No teams created yet
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                Create your first team to start organizing users
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                Create First Team
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 3,
              }}
            >
              {teams.map((team) => {
                const teamAssignable = getAssignableUsers(team);
                return (
                <Card
                  key={team.id}
                  sx={{
                    borderRadius: 3,
                    boxShadow: theme.shadows[2],
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardHeader
                    avatar={
                      <Box sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        borderRadius: '50%',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <GroupsIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                      </Box>
                    }
                    title={
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={600}>
                          {team.name}
                        </Typography>
                        {team.discipline_name ? (
                          <Chip
                            label={team.discipline_name}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        ) : (
                          <Chip
                            label="No discipline"
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                      </Stack>
                    }
                    subheader={
                      <Box>
                        {team.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                            {team.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {team.users.length} user{team.users.length !== 1 ? 's' : ''} assigned
                        </Typography>
                      </Box>
                    }
                    action={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Edit Team">
                          <IconButton size="small" onClick={() => handleOpen(team)} sx={{ color: 'primary.main' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Team">
                          <IconButton size="small" onClick={() => handleDelete(team.id)} sx={{ color: 'error.main' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                    sx={{ pb: 1 }}
                  />

                  <CardContent sx={{ pt: 0, flexGrow: 1 }}>
                    {/* Assign User Section */}
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FormControl size="small" sx={{ flexGrow: 1 }}>
                          <InputLabel>Assign User</InputLabel>
                          <Select
                            value={selectedUserByTeam[team.id] || ''}
                            onChange={(e) =>
                              setSelectedUserByTeam(prev => ({ ...prev, [team.id]: e.target.value }))
                            }
                            label="Assign User"
                          >
                            {teamAssignable.length === 0 ? (
                              <MenuItem value="" disabled>
                                {team.discipline_id
                                  ? `No eligible users with the ${team.discipline_name} discipline to add`
                                  : 'No eligible users to add'}
                              </MenuItem>
                            ) : (
                              teamAssignable.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                  {user.full_name || user.username}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PersonAddIcon />}
                          onClick={() => handleAssignUser(team.id)}
                          disabled={!selectedUserByTeam[team.id]}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Assign
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {team.discipline_id
                          ? `Showing users with the “${team.discipline_name}” discipline who aren’t already in this team.`
                          : 'Showing users who aren’t already in this team. Set a discipline to narrow the list further.'}
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Members Table */}
                    {team.users.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Disciplines</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Location Permissions</TableCell>
                              <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {team.users.map((user) => (
                              <TableRow key={user.id}>
                                <TableCell sx={{ maxWidth: '150px' }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={user.full_name || user.username}
                                  >
                                    {user.full_name || user.username}
                                  </Typography>
                                  {user.email && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      title={user.email}
                                    >
                                      {user.email}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {user.disciplines && user.disciplines.length > 0 ? (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Chip
                                        label={user.disciplines[0]}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                      />
                                      {user.disciplines.length > 1 && (
                                        <Tooltip
                                          title={
                                            <Box>
                                              <Typography variant="caption" fontWeight={600} display="block">
                                                All Disciplines:
                                              </Typography>
                                              {user.disciplines.map((discipline, index) => (
                                                <Typography key={index} variant="caption" display="block">
                                                  • {discipline}
                                                </Typography>
                                              ))}
                                            </Box>
                                          }
                                          arrow
                                        >
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                                          >
                                            +{user.disciplines.length - 1} more
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No disciplines
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ maxWidth: '200px' }}>
                                  {user.permission_scope === 'organization' ? (
                                    <Chip
                                      label="Organization Wide"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                  ) : user.permission_scope === 'network_section' && user.permissions && user.permissions.length > 0 ? (
                                    <Tooltip
                                      title={
                                        <Box>
                                          <Typography variant="caption" fontWeight={600} display="block">
                                            All Location Permissions:
                                          </Typography>
                                          {user.permissions.map((permission, index) => (
                                            <Typography key={index} variant="caption" display="block">
                                              • {permission}
                                            </Typography>
                                          ))}
                                        </Box>
                                      }
                                      arrow
                                    >
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                      >
                                        {user.permissions.join(', ')}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No permissions
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Remove from Team">
                                    <IconButton size="small" onClick={() => handleUnassignUser(team.id, user.id, user.full_name || user.username)} sx={{ color: 'error.main' }}>
                                      <PersonRemoveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 3,
                          backgroundColor: alpha(theme.palette.grey[100], 0.5),
                          borderRadius: 2,
                          border: `1px dashed ${alpha(theme.palette.grey[400], 0.5)}`
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No users assigned to this team
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          Use the form above to assign users
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Create / Edit Team dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <GroupsIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {selectedTeam ? 'Edit Team' : 'Create Team'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField
              autoFocus
              label="Team Name"
              type="text"
              fullWidth
              variant="outlined"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name..."
              sx={{ mt: 1 }}
            />
            <FormControl fullWidth variant="outlined" required>
              <InputLabel>Discipline</InputLabel>
              <Select
                value={teamDisciplineId}
                onChange={(e) => setTeamDisciplineId(e.target.value)}
                label="Discipline"
              >
                {availableDisciplines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              value={teamDescription}
              onChange={(e) => setTeamDescription(e.target.value)}
              placeholder="Optional description of the team's purpose..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!teamName.trim() || !teamDisciplineId}
            sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
          >
            {selectedTeam ? 'Update Team' : 'Create Team'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TeamManagementPanel;
